import React, { useEffect, useRef, useState } from 'react';

interface KPIItem {
  value: string;
  label: string;
  suffix?: string;
}

interface KPIStripProps {
  items: KPIItem[];
  background?: 'dark' | 'accent' | 'navy';
}

export const KPIStrip: React.FC<KPIStripProps> = ({ items, background = 'dark' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const backgrounds = {
    dark: 'bg-slate-900',
    accent: 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-y border-orange-600/30',
    navy: 'bg-slate-950',
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, []);

  return (
    <div ref={ref} className={`${backgrounds[background]} py-10 md:py-14`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-2 md:grid-cols-${items.length > 4 ? '4' : items.length} gap-6 md:gap-8`}>
          {items.map((item, index) => (
            <div 
              key={index} 
              className={`text-center transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.value}
                {item.suffix && <span className="text-orange-500">{item.suffix}</span>}
              </div>
              <div className="text-slate-400 text-xs md:text-sm font-medium uppercase tracking-wider">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
